/**
 * Applies the brand pack: theme tokens as CSS variables (light on :root, dark on
 * .dark), radius, font, document title and icons. Mirrors the server contract in
 * src/_config/brand-config.types.ts (ALLOWED_CSS_VARS): unknown tokens are ignored
 * and values are sanitised, so a bad pack can never inject arbitrary CSS.
 */
import type { BrandPresentation, BrandTheme } from '../runtime-config';

export const ALLOWED_THEME_TOKENS = new Set([
  'background',
  'foreground',
  'card',
  'card-foreground',
  'popover',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'border',
  'input',
  'ring',
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'sidebar',
  'sidebar-foreground',
  'sidebar-primary',
  'sidebar-primary-foreground',
  'sidebar-accent',
  'sidebar-accent-foreground',
  'sidebar-border',
  'sidebar-ring',
  'success',
  'success-foreground',
  'warning',
  'warning-foreground',
  'info',
  'info-foreground',
  'neutral',
  'neutral-foreground',
]);

const SAFE_VALUE = /^[#a-zA-Z0-9%().,/\s+-]{1,80}$/;
const SAFE_FONT = /^[a-zA-Z0-9 ,'"-]{1,120}$/;
const STYLE_ID = 'brand-theme-vars';

/** Colours and lengths only: no statement breaks, and no url()/expression() even inside a custom property. */
export const isSafeCssValue = (v: string) => SAFE_VALUE.test(v) && !/url\(|expression\(/i.test(v);

function declarations(tokens: Record<string, string>, radius?: string): string {
  const out: string[] = [];
  for (const [name, value] of Object.entries(tokens)) {
    if (ALLOWED_THEME_TOKENS.has(name) && isSafeCssValue(value)) out.push(`--${name}: ${value};`);
  }
  if (radius && isSafeCssValue(radius)) out.push(`--radius: ${radius};`);
  return out.join(' ');
}

export function buildThemeCss(theme: BrandTheme): string {
  return `:root { ${declarations(theme.light, theme.radius)} }\n.dark { ${declarations(theme.dark, theme.radius)} }`;
}

/** Only same-origin, root-relative asset URLs render: the CSP allows img-src 'self' data: only. */
export const isRenderableAssetUrl = (url: string | undefined): url is string => !!url && /^\/(?!\/)/.test(url);

function setLink(doc: Document, rel: string, href: string) {
  let link = doc.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = doc.createElement('link');
    link.rel = rel;
    doc.head.appendChild(link);
  }
  link.href = href;
}

export function applyBrand(presentation: BrandPresentation, doc: Document = document): void {
  let style = doc.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = doc.createElement('style');
    style.id = STYLE_ID;
    doc.head.appendChild(style);
  }
  style.textContent = buildThemeCss(presentation.theme);

  const font = presentation.theme.fontFamily;
  if (font && SAFE_FONT.test(font)) doc.body.style.fontFamily = font;

  doc.title = presentation.brand.documentTitle || presentation.brand.productName;
  if (isRenderableAssetUrl(presentation.brand.faviconUrl)) setLink(doc, 'icon', presentation.brand.faviconUrl);
  if (isRenderableAssetUrl(presentation.brand.appleTouchIconUrl)) {
    setLink(doc, 'apple-touch-icon', presentation.brand.appleTouchIconUrl);
  }
}
